import React, {useEffect, useState} from "react"
import Parse from "parse"
import { Stack, Typography, Divider } from "@mui/material"
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined"
import PeopleIcon from "@mui/icons-material/People"
import { COLORS } from "../../../utils"
import { getUserEventData, formatDate, formatTime } from "../../../utils/flow"

export default function PseProperties({pseId}) {
    const [pse, setPse] = useState(null)
    const [userEvents, setUserEvents] = useState([])

    useEffect(() => {
        const load = async () => {
            if (!pseId) return
            const loadedPse = await new Parse.Query("ProductionStepExecutions")
                .equalTo("objectId", pseId)
                .select(
                    "theoreticalNetWeight", "theoreticalGrossWeight", "expectedProduction", "netWeight",
                    "theoreticalStartTime", "theoreticalEndTime", "startTime", "endTime",
                    "productionStepSnapshot.kitchenArea.name", "productionStepSnapshot.transformation",
                    "productionDate", "stepProductionDate", "events"
                )
                .first({json: true})
            if (loadedPse) {
                setPse(loadedPse)
                // Process events to extract user actions
                const transformation = loadedPse.productionStepSnapshot?.transformation
                const processedEvents = await getUserEventData(loadedPse.events, transformation)
                setUserEvents(processedEvents)
            }
        }
        load()
    }, [pseId])


    return (
        <Stack spacing={"24px"}>
            {/* Propriétés Section */}
            {pse && (
                <Stack spacing={2} style={{border:`1px solid ${COLORS.BLACK_300}`, borderRadius: "8px", padding: "16px"}}>
                    <SectionTitle
                        icon={<SettingsOutlinedIcon/>} 
                        title="Propriétés" 
                    />
                    <Stack spacing={2} style={{backgroundColor: COLORS.BACKGROUND_GREY, borderRadius:"8px", padding:"16px"}}>
                        <PropertyRow label="ID" value={pse.objectId} />
                        <PropertyRow label="Atelier" value={pse.productionStepSnapshot?.kitchenArea?.name} />
                        <PropertyRow label="Production attendue" value={pse.expectedProduction} />
                        <PropertyRow label="Poids brut théorique" value={Math.round(pse.theoreticalGrossWeight * 1000) / 1000 || "-"} />
                        <PropertyRow label="Poids net théorique" value={Math.round(pse.theoreticalNetWeight * 1000) / 1000 || "-"} />
                        <PropertyRow label="Poids pesé" value={Math.round(pse.netWeight * 1000) / 1000 || "-"} />
                        <PropertyRow label="Heures théoriques" value={
                            pse.theoreticalStartTime && pse.theoreticalEndTime 
                                ? `${formatTime(pse.theoreticalStartTime)} - ${formatTime(pse.theoreticalEndTime)}`
                                : null
                        } />
                        <PropertyRow label="Heures réelles" value={
                            pse.startTime && pse.endTime
                                ? `${formatTime(pse.startTime)} (${formatDate(pse.startTime)}) - ${formatTime(pse.endTime)} (${formatDate(pse.endTime)})`
                                : null
                        } />
                        <PropertyRow label="Jour de réalisation" value={formatDate(pse.stepProductionDate)} />
                    </Stack>
                </Stack>
            )}

            {/* Utilisateurs Section */}
            <Stack spacing={2} style={{border:`1px solid ${COLORS.BLACK_300}`, borderRadius: "8px", padding: "16px", marginBottom: "16px"}}>
                <SectionTitle 
                    icon={<PeopleIcon sx={{ fontSize: "24px", color: COLORS.BLACK_700 }} />} 
                    title="Utilisateurs" 
                />
                {userEvents.length > 0 ? (
                    <Stack spacing={2} style={{backgroundColor: COLORS.BACKGROUND_GREY, borderRadius:"8px", padding:"16px"}}>
                        {userEvents.map((event, index) => (
                            <UserEventRow 
                                key={index} 
                                label={event.label} 
                                user={event.user} 
                                detail={event.detail}
                            />
                        ))}
                    </Stack>
                ) : (
                    <Stack style={{backgroundColor: COLORS.BACKGROUND_GREY, borderRadius:"8px", padding:"16px"}}>
                        <Typography sx={{ fontSize: "14px", lineHeight: "20px", color: COLORS.BLACK_600, fontStyle: "italic", textAlign: "center" }}>
                            Aucun événement utilisateur enregistré
                        </Typography>
                    </Stack>
                )}
            </Stack>
        </Stack>
    )
}

const PropertyRow = ({ label, value }) => (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={"34px"}>
        <Typography sx={{ minWidth: "180px", fontSize:"16px", lineHeight:"24px", color: COLORS.BLACK_700 }}>
            {label}
        </Typography>
        <Typography sx={{ minWidth: "auto", fontSize:"16px", lineHeight:"24px", color: COLORS.BLACK800, textAlign: "right" }}>
            {value != null ? value : "-"}
        </Typography>
    </Stack>
)

const UserEventRow = ({ label, user, detail }) => (
    <Stack spacing={0.5} direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="column" alignItems="left" spacing={0} sx={{ width: "50%" }}>
            <Typography sx={{ fontSize:"16px", lineHeight:"24px", color: COLORS.BLACK_700, minWidth: "auto" }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: "14px", lineHeight: "22px", color: COLORS.BLACK_500, paddingLeft: 0 }}>
                {detail}
            </Typography>
        </Stack>
        <Divider orientation="vertical" flexItem sx={{ mx: 2, borderColor: COLORS.BLACK_300, height: "32px", alignSelf: "center" }} />
        <Typography sx={{ fontSize:"16px", lineHeight:"24px", color: COLORS.BLACK800, flex: 1, textAlign: "right" }}>
            {user}
        </Typography>
    </Stack>
)

const SectionTitle = ({ icon, title }) => (
    <Stack direction="row" alignItems="center" spacing={1}>
        <span style={{ height:"40px", padding: "8px", color: COLORS.BLACK_700, backgroundColor:COLORS.BACKGROUND_LIGHT_GREY, borderRadius: "8px" }}>
            {icon}
        </span>
        <Typography sx={{ fontSize: "16px", lineHeight:"24px", fontWeight: 600, color: COLORS.BLACK800 }}>
            {title}
        </Typography>
    </Stack>
)